import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from '../hooks/useMediaQuery';

/**
 * PenaltyBadge — the "+N" pill shown in a round card's PEN column.
 *
 * Desktop (hover-capable pointer): hovering shows the reason in a floating
 * tooltip; clicking pins it open (click again, click outside, or Esc to close).
 * Mobile / touch: tapping opens a bottom sheet with the penalty and its reason,
 * which is far more legible than a hover tooltip on a narrow screen.
 *
 * The popover is rendered through a portal so it is never clipped by the
 * horizontally-scrolling table wrapper that hosts the round card.
 */

interface PenaltyBadgeProps {
  strokes: number;
  reason?: string | null;
  playerName?: string;
}

const POPOVER_WIDTH = 260;
const VIEWPORT_GUTTER = 8;

const styles: Record<string, React.CSSProperties> = {
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4em',
    background: '#fff7ed',
    color: '#c2410c',
    border: '1px solid #fdba74',
    borderRadius: 999,
    padding: '0.15em 0.65em 0.15em 0.75em',
    fontSize: '0.92em',
    fontWeight: 700,
    lineHeight: 1.4,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
  badgeOpen: {
    background: '#ffedd5',
    borderColor: '#fb923c',
    boxShadow: '0 0 0 2px rgba(251,146,60,0.25)',
  },
  infoDot: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.1em',
    height: '1.1em',
    borderRadius: '50%',
    background: '#fb923c',
    color: '#fff',
    fontSize: '0.7em',
    fontWeight: 800,
    fontStyle: 'italic',
    fontFamily: 'Georgia, "Times New Roman", serif',
    lineHeight: 1,
  },
  popover: {
    position: 'fixed',
    zIndex: 3000,
    width: POPOVER_WIDTH,
    background: '#0f172a',
    color: '#f1f5f9',
    borderRadius: 10,
    padding: '10px 12px',
    boxShadow: '0 12px 32px rgba(15,23,42,0.35)',
    fontSize: 12,
    lineHeight: 1.45,
    textAlign: 'left',
  },
  popoverLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#fdba74',
    marginBottom: 4,
  },
  popoverReason: {
    color: '#e2e8f0',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.5)',
    zIndex: 3000,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  sheet: {
    width: '100%',
    maxWidth: 560,
    background: '#fff',
    borderRadius: '16px 16px 0 0',
    padding: '14px 18px calc(18px + env(safe-area-inset-bottom, 0px))',
    boxShadow: '0 -8px 32px rgba(15,23,42,0.25)',
    color: '#0f172a',
    textAlign: 'left',
  },
  sheetHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    background: '#e2e8f0',
    margin: '0 auto 12px',
  },
  sheetTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  sheetTitle: {
    fontSize: 15,
    fontWeight: 800,
    margin: 0,
  },
  sheetPlayer: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: 600,
  },
  sheetLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#c2410c',
    marginBottom: 4,
  },
  sheetReason: {
    fontSize: 14,
    lineHeight: 1.5,
    color: '#1e293b',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: 10,
    padding: '10px 12px',
  },
  sheetClose: {
    marginTop: 14,
    width: '100%',
    border: '1px solid #e2e8f0',
    background: '#f8fafc',
    color: '#0f172a',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

const PenaltyBadge: React.FC<PenaltyBadgeProps> = ({ strokes, reason, playerName }) => {
  const isMobile = useIsMobile();
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [hovering, setHovering] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const open = isMobile ? pinned : (pinned || hovering);
  const reasonText = (reason || '').trim() || 'No reason recorded.';
  const strokeLabel = `${strokes} penalty stroke${strokes === 1 ? '' : 's'}`;

  const close = useCallback(() => { setPinned(false); setHovering(false); }, []);

  // Desktop: anchor the tooltip under the badge (or above it when there's no
  // room below), clamped inside the viewport.
  useLayoutEffect(() => {
    if (isMobile || !open) { setPos(null); return; }
    const btn = btnRef.current;
    const pop = popRef.current;
    if (!btn || !pop) return;
    const r = btn.getBoundingClientRect();
    const h = pop.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = r.left + r.width / 2 - POPOVER_WIDTH / 2;
    left = Math.max(VIEWPORT_GUTTER, Math.min(left, vw - POPOVER_WIDTH - VIEWPORT_GUTTER));
    let top = r.bottom + 8;
    if (top + h > vh - VIEWPORT_GUTTER) top = Math.max(VIEWPORT_GUTTER, r.top - 8 - h);
    setPos({ top, left });
  }, [open, isMobile, reasonText]);

  // Close on outside click, Esc, scroll or resize (a fixed tooltip would
  // otherwise drift away from its badge while the table scrolls).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    const onScroll = () => { if (!isMobile) close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, isMobile, close]);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPinned(p => !p);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        style={{ ...styles.badge, ...(open ? styles.badgeOpen : {}) }}
        onClick={toggle}
        onMouseEnter={() => { if (!isMobile) setHovering(true); }}
        onMouseLeave={() => { if (!isMobile) setHovering(false); }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${strokeLabel}${playerName ? ` for ${playerName}` : ''} — show reason`}
      >
        +{strokes}
        <span style={styles.infoDot} aria-hidden>i</span>
      </button>

      {open && !isMobile && createPortal(
        <div
          ref={popRef}
          role="tooltip"
          style={{
            ...styles.popover,
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          <div style={styles.popoverLabel}>
            Penalty · +{strokes} stroke{strokes === 1 ? '' : 's'}
            {playerName ? <span style={{ color: '#94a3b8', fontWeight: 600, letterSpacing: 0, textTransform: 'none', marginLeft: 6 }}>{playerName}</span> : null}
          </div>
          <div style={styles.popoverReason}>{reasonText}</div>
          <div style={{ marginTop: 6, fontSize: 10, color: '#94a3b8' }}>Added to the net score after handicap.</div>
        </div>,
        document.body,
      )}

      {open && isMobile && createPortal(
        <div style={styles.backdrop} onClick={close} role="presentation">
          <div
            style={styles.sheet}
            role="dialog"
            aria-modal="true"
            aria-label={`${strokeLabel}${playerName ? ` for ${playerName}` : ''}`}
            onClick={e => e.stopPropagation()}
          >
            <div style={styles.sheetHandle} />
            <div style={styles.sheetTitleRow}>
              <span style={{ ...styles.badge, cursor: 'default' }}>+{strokes}</span>
              <div>
                <h3 style={styles.sheetTitle}>Penalty · {strokeLabel}</h3>
                {playerName && <div style={styles.sheetPlayer}>{playerName}</div>}
              </div>
            </div>
            <div style={styles.sheetLabel}>Reason</div>
            <div style={styles.sheetReason}>{reasonText}</div>
            <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
              Penalty strokes are added to the net score after the handicap.
            </div>
            <button type="button" style={styles.sheetClose} onClick={close}>Close</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default PenaltyBadge;
